import { Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { KeycloakService } from "keycloak-angular";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { environment } from "@env";
import { KeycloakStateService } from "src/app/core/service/utility.service";

type SharedWorkflowKey =
  | 'systemAssetCapture'
  | 'dataAssetCapture'
  | 'dataAssetConfiguration'
   | 'securityZoneManagement';

interface SharedWorkflowNavigationItem {
  key: SharedWorkflowKey;
  path: string;
}

interface SharedWorkflowProductConfig {
  routePrefix: string;
  basePath: string;
  title: string;
  workflows: SharedWorkflowNavigationItem[];
}

@Component({
  selector: "app-side-navbar",
  templateUrl: "./side-navbar.component.html",
  styleUrls: ["./side-navbar.component.scss"],
})
export class SideNavbarComponent implements OnInit, OnDestroy {
  activeLink: string = "";
  isTitle: string = "";
  roles: string[] = [];
  private routerSubscription: Subscription = new Subscription();

  private readonly sharedWorkflowProductConfigs: SharedWorkflowProductConfig[] = [
    {
      routePrefix: '/asset-management/auth-tools',
      basePath: '/asset-management/auth-tools',
      title: 'Asset Management',
      workflows: [
        {
          key: 'systemAssetCapture',
          path: 'system-asset-capture'
        },
        {
          key: 'dataAssetCapture',
          path: 'data-asset-capture'
        },
        {
          key: 'dataAssetConfiguration',
          path: 'data-asset-configuration'
        }


      ]
    },

    {
      routePrefix: '/data-protection/auth-tools',
      basePath: '/data-protection/auth-tools',
      title: 'Data Protection',
      workflows: [
        {
          key: 'systemAssetCapture',
          path: 'system-asset-capture'
        },
        {
          key: 'dataAssetCapture',
          path: 'data-asset-capture'
        },
        {
          key: 'dataAssetConfiguration',
          path: 'data-asset-configuration'
        },
          {
          key: 'securityZoneManagement',
          path: 'security-zone-management'
        }
      ]
    },

          {
        routePrefix: '/secure-zone',
        basePath: '/secure-zone',
        title: 'Security Zone',
        workflows: [
          {
            key: 'securityZoneManagement',
            path: 'security-zone-management',
          },
        ],
      },

    // Add future products here with only the workflows they need.
  ];

  private getCurrentSharedWorkflowProductConfig():
    SharedWorkflowProductConfig | undefined {

    const currentUrl = this.router.url.split('?')[0].split('#')[0];


    return this.sharedWorkflowProductConfigs.find(config =>
      currentUrl === config.routePrefix ||
      currentUrl.startsWith(`${config.routePrefix}/`)
    );
  }

  hasSharedWorkflow(workflowKey: SharedWorkflowKey): boolean {
    const config = this.getCurrentSharedWorkflowProductConfig();

    return config?.workflows.some(
      workflow => workflow.key === workflowKey
    ) ?? false;
  }

private navigateToSharedWorkflow(
  workflowKey: SharedWorkflowKey,
  childSegments: string[] = []
): void {
  const config = this.getCurrentSharedWorkflowProductConfig();

  if (!config) {
    return;
  }

  const workflow = config.workflows.find(
    item => item.key === workflowKey
  );

  if (!workflow) {
    return;
  }

  const workflowBasePath =
    `${config.basePath.replace(/\/+$/, '')}/${workflow.path}`;

  const targetUrl = [
    workflowBasePath,
    ...childSegments,
  ]
    .join('/')
    .replace(/\/+/g, '/');

  void this.router.navigateByUrl(targetUrl);
}

  constructor(private router: Router, private keycloakservice: KeycloakService, private keycloakStateService: KeycloakStateService) { }

  ngOnInit(): void {
    this.keycloakStateService.observeRealmRoles().subscribe((roles) => {
      this.roles = roles || [];
    });
    // Subscribe to route changes
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setActiveLinkBasedOnRoute();
      });

    // Set the active link on initial load
    this.setActiveLinkBasedOnRoute();
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  getUserRoles(): string[] {
    return this.keycloakStateService.getRealmRoles();
  }

  hasRole(role: string): boolean {
    return this.roles.includes(role.toLowerCase());
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(r => this.hasRole(r));
  }

setActiveLinkBasedOnRoute(): void {
  const currentUrl = this.router.url.split('?')[0].split('#')[0];

  const workflowProductConfig =
    this.getCurrentSharedWorkflowProductConfig();

  // ============================================================
  // 1. Handle Security Zone Management and its child routes
  // ============================================================

  const securityZoneWorkflow =
    workflowProductConfig?.workflows.find(
      workflow => workflow.key === 'securityZoneManagement'
    );

  if (workflowProductConfig && securityZoneWorkflow) {
    const securityZoneBasePath =
      `${workflowProductConfig.basePath.replace(/\/+$/, '')}/${securityZoneWorkflow.path}`;

    const isSecurityZoneRoute =
      currentUrl === securityZoneBasePath ||
      currentUrl.startsWith(`${securityZoneBasePath}/`);

    if (isSecurityZoneRoute) {
      const relativePath = currentUrl
        .slice(securityZoneBasePath.length)
        .replace(/^\/+/, '');

      const section = relativePath.split('/')[0];

      switch (section) {
        case 'environment':
          this.setActiveLink('zoneManagementEnvironment');
          break;

        case 'zone':
          this.setActiveLink('zoneManagementZone');
          break;

        case 'platform':
          this.setActiveLink('zoneManagementPlatform');
          break;

        default:
          // Workflow root or another route without a recognized section.
          this.setActiveLink('securityZoneManagement');
          break;
      }

      this.isTitle = workflowProductConfig.title;

      // Keep the parent dropdown open when viewing any child route.
      this.isZoneManagementDropdownOpen = true;

      return;
    }
  }

  // ============================================================
  // 2. Handle other reusable workflows
  // ============================================================

  const activeWorkflow =
    workflowProductConfig?.workflows.find(
      workflow =>
        workflow.key !== 'securityZoneManagement' &&
        currentUrl.includes(`/${workflow.path}`)
    );

  if (workflowProductConfig && activeWorkflow) {
    this.setActiveLink(activeWorkflow.key);
    this.isTitle = workflowProductConfig.title;

    return;
  }

  // ============================================================
  // 3. Adaptive Access Control
  // ============================================================

  if (this.isAdaAccessRouteNavbar()) {
    if (currentUrl.includes('policy-authoring')) {
      this.setActiveLink('aacPolicyAuth');
    } else if (currentUrl.includes('access-flow')) {
      this.setActiveLink('accessFlowAuth');
    } else if (currentUrl.includes('adaptiveaccessnodeconfiguration')) {
      this.setActiveLink('adaptiveAccessNodeConf');
    } else if (currentUrl.includes('adaptiveaccessnode')) {
      this.setActiveLink('adaptiveAccessNodeAuth');
    } else if (currentUrl.includes('access-control-reports')) {
      this.setActiveLink('adaptiveAccessReport');
    } else {
      this.setActiveLink('dashboardAAC');
    }

    this.isTitle = 'Adaptive Access Control';
  }

  // ============================================================
  // 4. Asset Management
  // ============================================================

  else if (this.isAssetManagementRouteNavbar()) {
    if (currentUrl.includes('assets-view')) {
      this.setActiveLink('assetsView');
    } else if (currentUrl.includes('asset-management-reports')) {
      this.setActiveLink('assetManagementReport');
    } else {
      this.setActiveLink('dashboardAM');
    }

    this.isTitle = 'Asset Management';
  }

  // ============================================================
  // 5. Digital Identity Management
  // ============================================================

  else if (this.isDigitalIdentityRouteNavbar()) {
    if (currentUrl.includes('identity-provisioning')) {
      this.setActiveLink('identityProvisioning');
    } else if (currentUrl.includes('identity-admin-panel')) {
      this.setActiveLink('identityaAdminPanel');
    } else if (currentUrl.includes('audit-trail-auth')) {
      this.setActiveLink('auditTrailAuthoring');
    } else if (currentUrl.includes('digital-identity-reports')) {
      this.setActiveLink('digitalIdentityReport');
    } else {
      this.setActiveLink('dashboardDI');
    }

    this.isTitle = 'Digital Identity Management';
  }

  // ============================================================
  // 6. Data Protection
  // ============================================================

  else if (this.isDataProtectionRouteNavbar()) {
    if (currentUrl.includes('data-asset-mapping')) {
      this.setActiveLink('dataAssetMapping');
    } 
    else if(currentUrl.includes('data-protection-program')){
       this.setActiveLink('dataProtectionProgram');
    }
    else if (currentUrl.includes('data-protection-reports')) {
      this.setActiveLink('dataProtectionReport');
    } else {
      this.setActiveLink('dashboardDP');
    }


    this.isTitle = 'Data Protection';
  }

  // ============================================================
  // 7. DSO Guard
  // ============================================================

  else if (this.isDsoGuardRouteNavbar()) {
    this.setActiveLink('dashboardDsoGuard');
    this.isTitle = 'DSO Guard';
  }

  // ============================================================
  // 8. Secure Blockchain
  // ============================================================

  else if (this.isSecureBlockchainRouteNavbar()) {
    if (currentUrl.includes('identity')) {
      this.setActiveLink('identityManagement');
    } else if (currentUrl.includes('access-flow')) {
      this.setActiveLink('accessFlow');
    } else if (currentUrl.includes('smart-contract')) {
      this.setActiveLink('smartContractManagement');
    } else if (currentUrl.includes('operation')) {
      this.setActiveLink('smartContractOperation');
    } else if (currentUrl.includes('view')) {
      this.setActiveLink('smartContractView');
    } else {
      this.setActiveLink('dashboardSecureBlockchain');
    }

    this.isTitle = 'Secure Blockchain';
  }

  // ============================================================
  // 9. Insight eGRC
  // ============================================================

  else if (this.isInsightEgrcRouteNavbar()) {
    if (currentUrl.includes('tech-stack-control/search')) {
      this.setActiveLink('techStackControlSearch');
    } else if (
      currentUrl.includes('risk-management/risk-register-search')
    ) {
      this.setActiveLink('riskManagementRiskRegister');
    } else {
      this.setActiveLink('dashboardInsightEgrc');
    }

    this.isTitle = 'EGRC';
  }

  // ============================================================
  // 10. Standalone / Legacy Secure Zone routes
  // ============================================================

  else if (this.isSecureZoneRouteNavbar()) {
    if (currentUrl.includes('/secure-zone/environment')) {
      this.setActiveLink('zoneManagementEnvironment');
    } else if (currentUrl.includes('/secure-zone/zone')) {
      this.setActiveLink('zoneManagementZone');
    } else if (currentUrl.includes('/secure-zone/platform')) {
      this.setActiveLink('zoneManagementPlatform');
    } else {
      this.setActiveLink('dashboardSecureZone');
    }

    this.isTitle = 'Security Zone';
  }
}

  setActiveLink(link: string): void {
    this.activeLink = link;
  }

  // Navigation methods for each tool

  //Adaptive Access Control Navigation
  aacPolicyAuth() {
    this.router.navigate(["ada-access/auth-tools/policy-authoring"]);
  }

  dashboardAAC() {
    this.router.navigate(["ada-access/auth-tools"]);
  }
  accessFlowAuth() {
    this.router.navigate(["ada-access/auth-tools/access-flow/search"]);
  }

  adaptiveAccessNodeAuth() {
    this.router.navigate(["ada-access/auth-tools/adaptiveaccessnode/search"]);
  }

  adaptiveAccessNodeConf() {
    this.router.navigate([
      "ada-access/auth-tools/adaptiveaccessnodeconfiguration/search",
    ]);
  }

  adaptiveAccessReport() {
    this.router.navigate(["ada-access/auth-tools/access-control-reports"]);
  }

  //Asset Management Navigation

  dashboardAM() {
    this.router.navigate(["asset-management/auth-tools"]);
  }

  // Reusable Asset Workflow Navigation

  systemAssetCapture(): void {
    this.navigateToSharedWorkflow('systemAssetCapture');
  }

  dataAssetCapture(): void {
    this.navigateToSharedWorkflow('dataAssetCapture');
  }

  dataAssetConfiguration(): void {
    this.navigateToSharedWorkflow('dataAssetConfiguration');
  }

  assetsView() {
    this.router.navigate(["asset-management/auth-tools/assets-view"]);
  }


  assetManagementReport() {
    this.router.navigate([
      "asset-management/auth-tools/asset-management-reports",
    ]);
  }

  //Digital Identity Navigation

  dashboardDI() {
    this.router.navigate(["digital-identity/auth-tools"]);
  }

  identityProvisioning() {
    this.router.navigate(["digital-identity/auth-tools/identity-provisioning"]);
  }

  auditTrailAuthoring() {
    this.router.navigate(["digital-identity/auth-tools/audit-trail-auth"]);
  }
  identityaAdminPanel() {
    this.router.navigate(["digital-identity/auth-tools/identity-admin-panel"]);
  }
  digitalIdentityReport() {
    this.router.navigate([
      "digital-identity/auth-tools/digital-identity-reports",
    ]);
  }

  //Data Protection Navigation

  dashboardDP() {
    this.router.navigate(["data-protection/auth-tools"]);
  }


  dataAssetMapping() {
    this.router.navigate([
      "data-protection/auth-tools/data-asset-mapping",
    ]);
  }

  dataProtectionProgram(){
    this.router.navigate([
      "data-protection/auth-tools/data-protection-program",
    ]);
  }

  dataProtectionReport() {
    this.router.navigate([
      "data-protection/auth-tools/data-protection-reports",
    ]);
  }





  //DSO Guard Navigation

  dashboardDsoGuard() {
    this.router.navigate(["dso-guard/auth-tools"]);
  }

  //Secure Blockchain Navigation

  // Identity
  canAccessIdentity(): boolean {
    return this.hasAnyRole([
      'identity_manager',
      'Ztaas_admin'
    ]);
  }

  // Access Flow
  canAccessAccessFlow(): boolean {
    return this.hasAnyRole([
      'access_control_manager',
      'Ztaas_admin'
    ]);
  }

  // Smart Contract Management
  canAccessSmartContract(): boolean {
    return this.hasAnyRole([
      'smart_contract_developer',
      'Ztaas_admin'
    ]);
  }

  // Operation
  canAccessOperation(): boolean {
    return this.hasAnyRole([
      'smart_contract_operator',
      'Ztaas_admin'
    ]);
  }

  // View
  canAccessView(): boolean {
    return this.hasAnyRole([
      'smart_contract_viewer',
      'Ztaas_admin'
    ]);
  }

  dashboardSecureBlockchain() {
    this.router.navigate(["secure-blockchain/auth-tools"]);
  }

  identityManagement() {
    this.router.navigate(["secure-blockchain/auth-tools/identity/search"]);
  }

  accessFlow() {
    this.router.navigate(["secure-blockchain/auth-tools/access-flow/search"]);
  }

  smartContractManagement() {
    this.router.navigate(["secure-blockchain/auth-tools/smart-contract/search"]);
  }

  smartContractOperation() {
    this.router.navigate(["secure-blockchain/auth-tools/operation/search"]);
  }

  smartContractView() {
    this.router.navigate(["secure-blockchain/auth-tools/view/search"]);
  }





  //Insight eGRC Navigation

  dashboardInsightEgrc() {
    this.router.navigate(["insight-egrc/auth-tools"]);
  }

  techStackControlSearch() {
    this.router.navigate(["insight-egrc/auth-tools/tech-stack-control/search"]);
  }

  isGovernanceDropdownOpen: boolean = false;

  toggleGovernanceDropdown(): void {
    this.isGovernanceDropdownOpen = !this.isGovernanceDropdownOpen;
  }

  isControlManagementDropdownOpen: boolean = false;

  toggleControlManagementDropdown(): void {
    this.isControlManagementDropdownOpen = !this.isControlManagementDropdownOpen;
  }

  isRiskManagementDropdownOpen: boolean = false;

  toggleRiskManagementDropdown(): void {
    this.isRiskManagementDropdownOpen = !this.isRiskManagementDropdownOpen;
  }

  riskManagementRiskRegister() {
    this.router.navigate(["insight-egrc/auth-tools/risk-management/risk-register-search"]);
  }

  //Secure Zone Navigation

  dashboardSecureZone() {
    this.router.navigate(["secure-zone"]);
  }

   
securityZoneManagement(): void {
  this.navigateToSharedWorkflow('securityZoneManagement');
}

// Environment subsection
zoneManagementEnvironment(): void {
  this.navigateToSharedWorkflow(
    'securityZoneManagement',
    ['environment', 'search']
  );
}

// Zone subsection
zoneManagementZone(): void {
  this.navigateToSharedWorkflow(
    'securityZoneManagement',
    ['zone', 'search']
  );
}

// Platform subsection
zoneManagementPlatform(): void {
  this.navigateToSharedWorkflow(
    'securityZoneManagement',
    ['platform', 'search']
  );
}

  isZoneManagementDropdownOpen: boolean = false;

  toggleZoneManagementDropdown(): void {
    this.isZoneManagementDropdownOpen = !this.isZoneManagementDropdownOpen;
  }


  // Helper methods to determine which route is active for conditional rendering in the template

  isAdaAccessRouteNavbar(): boolean {
    return this.router.url.includes("/ada-access/auth-tools");
  }

  isAssetManagementRouteNavbar(): boolean {
    return this.router.url.includes("/asset-management/auth-tools");
  }

  isDigitalIdentityRouteNavbar(): boolean {
    return this.router.url.includes("/digital-identity/auth-tools");
  }

  isDataProtectionRouteNavbar(): boolean {
    return this.router.url.includes("/data-protection/auth-tools");
  }

  isDsoGuardRouteNavbar(): boolean {
    return this.router.url.includes("/dso-guard/auth-tools");
  }

  isSecureBlockchainRouteNavbar(): boolean {
    return this.router.url.includes("/secure-blockchain/auth-tools");
  }

  isInsightEgrcRouteNavbar(): boolean {
    return this.router.url.includes("/insight-egrc/auth-tools");
  }

  isSecureZoneRouteNavbar(): boolean {
    return this.router.url.includes("/secure-zone");
  }

  logout(): void {
    this.keycloakservice.logout(environment.keyCloakRedirectUrl);
  }

  help() {
    if (this.isAdaAccessRouteNavbar()) {
      //this.router.navigate(['/ada-access/aac-tools'])
      window.open(
        `${environment.docsUrlRedirectUrl}/adaptiveAccessControl.html`,
        "_blank"
      );
    } else if (this.isAssetManagementRouteNavbar()) {
      window.open(
        `${environment.docsUrlRedirectUrl}/assetManagement.html`,
        "_blank"
      );
      //this.router.navigate(['/asset-management/tools'])
    } else if (this.isDigitalIdentityRouteNavbar()) {
      window.open(
        `${environment.docsUrlRedirectUrl}/digitalIdentity.html`,
        "_blank"
      )
    }
    else if (this.isDataProtectionRouteNavbar()) {
      window.open(
        `${environment.docsUrlRedirectUrl}/dataProtection.html`,
        "_blank"
      );
    }
    else if (this.isDsoGuardRouteNavbar()) {
      window.open(
        `${environment.docsUrlRedirectUrl}/dsoGuard.html`,
        "_blank"
      );
    }
    else if (this.isSecureBlockchainRouteNavbar()) {
      window.open(
        `${environment.docsUrlRedirectUrl}/secureBlockchain.html`,
        "_blank"
      );
    }
    else if (this.isInsightEgrcRouteNavbar()) {
      window.open(
        `${environment.docsUrlRedirectUrl}/insightEgrc.html`,
        "_blank"
      );
    }

  }

  back() {
    this.router.navigate(['/products']);
  }
}
